import {
  type Form,
  type FormField,
  type FormOverride,
  type FormRule,
  OverrideEntityType,
} from '@wix/ambassador-forms-v4-form/types';

import {
  fieldPathMapToPlatfomized,
  type SupportedPath,
  supportedPaths,
} from './transform-path-to-v2';
import { asRequired } from './utils';

type PropertiesOverridesByItem = {
  [entityId: string]: { path: string[]; value: any }[];
};
const PATH_SEPARATOR = '.';

export function applyItemPropertiesOverride(
  formSchema: Form,
  rule: FormRule,
): Form {
  if (rule.overrides) {
    const overriddenFormSchema = applyFormOverride(
      formSchema,
      mapOverridesByItem(rule.overrides, isFormOverride, isSupportedFormPath),
    );
    return {
      ...overriddenFormSchema,
      fields: applyItemsOverride(
        overriddenFormSchema.fields ?? [],
        mapOverridesByItem(
          rule.overrides,
          isFieldOverride,
          isSupportedFieldPath,
        ),
      ),
      ...(overriddenFormSchema.formFields && {
        formFields: applyItemsOverride(
          overriddenFormSchema.formFields ?? [],
          mapOverridesV2ByItem(rule.overrides, isFieldOverride),
        ),
      }),
    };
  }
  return formSchema;
}

function applyItemsOverride(
  items: { id?: string }[],
  formItemOverrides: PropertiesOverridesByItem,
): FormField[] | undefined {
  return items.map((formField) => {
    const fieldOverrides = formItemOverrides[asRequired(formField.id)] || [];
    return fieldOverrides.reduce(
      (acc, override) => setToValue(acc, override.path, override.value),
      formField,
    );
  });
}

function applyFormOverride(
  formSchema: Form,
  formItemOverrides: PropertiesOverridesByItem,
): Form {
  const formOverrides = Object.values(formItemOverrides).flat();
  return formOverrides.reduce(
    (acc, override) => setToValue(acc, override.path, override.value),
    formSchema,
  );
}

function mapOverridesByItem(
  overrideItems: FormOverride[],
  isOverride: (override: FormOverride) => Boolean,
  isSupportedPath: (path: string) => Boolean,
) {
  return overrideItems
    .filter(isOverride)
    .reduce<PropertiesOverridesByItem>((acc, overrideItem) => {
      const entityId = asRequired(overrideItem.entityId);
      const overrides = Object.entries(overrideItem.valueChanges ?? {})
        .filter(([path]) => isSupportedPath(path))
        .map(([path, value]) => ({
          path: path.split(PATH_SEPARATOR),
          value,
        }));

      acc[entityId] = [...(acc[entityId] || []), ...overrides];
      return acc;
    }, {});
}

function mapOverridesV2ByItem(
  overrideItems: FormOverride[],
  isOverride: (override: FormOverride) => Boolean,
) {
  return overrideItems
    .filter(isOverride)
    .reduce<PropertiesOverridesByItem>((acc, overrideItem) => {
      const entityId = asRequired(overrideItem.entityId);
      const overrides = Object.entries(overrideItem.valueChanges ?? {})
        .flatMap(([path, value]) => {
          const resolvedV2Paths =
            fieldPathMapToPlatfomized[path as SupportedPath] || [];
          return resolvedV2Paths.map((pathV2) => [pathV2, value]);
        })
        .map(([path, value]) => ({
          path: path.split(PATH_SEPARATOR),
          value,
        }));

      acc[entityId] = [...(acc[entityId] || []), ...overrides];
      return acc;
    }, {});
}

function setToValue(obj: any, pathProps: string[], value: unknown): any {
  if (value !== undefined && obj !== undefined) {
    const [prop, ...rest] = pathProps;
    if (rest.length) {
      const valueToSet = setToValue(obj[prop], rest, value);
      if (valueToSet !== undefined) {
        return {
          ...obj,
          [prop]: valueToSet,
        };
      }
      return obj;
    }
    return {
      ...obj,
      [prop]: value,
    };
  }
  return obj;
}

function isFieldOverride(override: FormOverride): Boolean {
  return override.entityType === OverrideEntityType.FIELD;
}

function isFormOverride(override: FormOverride): Boolean {
  return override.entityType === OverrideEntityType.FORM;
}

function isSupportedFieldPath(path: string): Boolean {
  return supportedPaths.includes(path);
}

function isSupportedFormPath(path: string): Boolean {
  return ['fields', 'steps'].includes(path);
}
